import { useState, useEffect, useMemo, useCallback } from "react"
import { useParams } from "react-router"
import { useGetAgentsApi, agentResponseToAgent } from "@/hooks/api"
import {
  useGetEvaluatorsApi,
  evaluatorsResponseToEvaluators,
} from "@/hooks/api/evaluators"
import {
  useListEvaluationCasesApi,
  useGetEvaluationResultsByCasesApi,
  useGetCaseEvaluatorInfosByCasesApi,
  useListEvaluationCaseLabelsApi,
  makeGetCaseEvaluatorInfosByCasesRequest,
  makeGetEvaluationResultsByCasesRequest,
  caseEvaluatorInfosResponseToData,
  evaluationCaseLabelResponseToData,
} from "@/hooks/api/eval"
import {
  CaseEvaluatorAssignment,
  CaseEvaluatorInfosByCase,
  EvaluationResult,
  AgentHistory,
  EvaluationCaseLabel,
} from "@/types"
import { Agent } from "@/types"
import {
  ALL_EVALUATORS_STORAGE_VALUE,
  Evaluator,
  EvaluationCase,
  MergedEvaluationCase,
  STORAGE_KEYS,
} from "../types"
import useToast from "@/hooks/useToast"
import { useTranslation } from "react-i18next"
import {
  transToEvaluationResults,
  transToEvaluationCases,
} from "@/hooks/api/types"
import { useStore } from "@/store"
import {
  getUserFacingApiErrorMessage,
  getUserFacingApiErrorToastProps,
} from "@/lib/apiErrors"

interface UseEvaluationDataReturn {
  // Agents
  agents: Agent[]
  currentAgent: Agent | undefined
  agentsError: ReturnType<typeof useGetAgentsApi>["error"]
  refreshAgents: () => void

  // Agent History
  selectedAgentHistory: AgentHistory | null
  setSelectedAgentHistory: (history: AgentHistory | null) => void

  // Evaluators
  evaluators: Evaluator[]
  selectedEvaluator: Evaluator | null
  setSelectedEvaluator: (evaluator: Evaluator | null) => void
  evaluatorsError: ReturnType<typeof useGetEvaluatorsApi>["error"]
  refreshEvaluators: () => void

  // Labels
  labels: EvaluationCaseLabel[]
  selectedLabelIds: string[]
  setSelectedLabelIds: React.Dispatch<React.SetStateAction<string[]>>
  refreshLabels: () => void
  getCaseSearchParams: () => URLSearchParams

  // Cases and Results
  cases: EvaluationCase[]
  totalCaseCount: number
  setCases: React.Dispatch<React.SetStateAction<EvaluationCase[]>>
  caseEvaluatorInfosByCase: Record<string, CaseEvaluatorAssignment[]>
  setCaseEvaluatorInfosByCase: React.Dispatch<
    React.SetStateAction<Record<string, CaseEvaluatorAssignment[]>>
  >
  results: EvaluationResult[]
  setResults: React.Dispatch<React.SetStateAction<EvaluationResult[]>>
  mergedCases: MergedEvaluationCase[]
  loading: boolean
  setLoading: (loading: boolean) => void
  error: string | null
  setError: (error: string | null) => void

  // API functions
  fetchCases: ReturnType<typeof useListEvaluationCasesApi>["fetchFn"]
  fetchEvaluationResultsForScope: (
    scope: EvaluationResultScope
  ) => Promise<EvaluationResult[]>
  fetchCaseEvaluatorInfos: ReturnType<
    typeof useGetCaseEvaluatorInfosByCasesApi
  >["fetchFn"]

  // Error handling
  handleApiErrors: () => void
}

interface UseEvaluationDataOptions {
  agents?: Agent[]
  currentAgent?: Agent
}

export interface EvaluationResultTarget {
  caseId: string
  evaluatorId: string
  agentHistoryId: string
}

export interface EvaluationResultScope {
  targets: EvaluationResultTarget[]
  includeOutput?: boolean
  includeReasoningSteps?: boolean
}

const getResultTargetKey = (
  caseId: string,
  evaluatorId: string,
  agentHistoryId?: string
) => `${caseId}:${evaluatorId}:${agentHistoryId ?? ""}`

const resultMatchesScope = (
  result: EvaluationResult,
  scope: EvaluationResultScope
) => {
  const targetKeys = new Set(
    scope.targets.map((target) =>
      getResultTargetKey(
        target.caseId,
        target.evaluatorId,
        target.agentHistoryId
      )
    )
  )
  return targetKeys.has(
    getResultTargetKey(
      result.evaluationCaseId,
      result.evaluatorId,
      result.agentHistoryId
    )
  )
}

const mergeEvaluationResultsForScope = (
  currentResults: EvaluationResult[],
  nextResults: EvaluationResult[],
  scope: EvaluationResultScope
) => [
  ...currentResults.filter((result) => !resultMatchesScope(result, scope)),
  ...nextResults,
]

export const useEvaluationData = (
  options: UseEvaluationDataOptions = {}
): UseEvaluationDataReturn => {
  const { workspaceId, agentId } = useParams()
  const { alertToast } = useToast()
  const { t } = useTranslation()
  const [userMe] = useStore((state) => [state.userMe])

  // Agent History state
  const [selectedAgentHistory, setSelectedAgentHistory] =
    useState<AgentHistory | null>(null)

  // Agents
  const {
    data: agentsResp,
    error: agentsError,
    fetchFn: getAgents,
  } = useGetAgentsApi()

  const refreshAgents = useCallback(() => {
    if (options.agents) return
    if (workspaceId && userMe?.profile?.defaultOrganizationId) {
      getAgents({
        searchParams: new URLSearchParams({
          wid: workspaceId,
          oid: userMe.profile.defaultOrganizationId,
        }),
      })
    }
  }, [options.agents, workspaceId, getAgents, userMe])

  const agents: Agent[] = useMemo(
    () => options.agents ?? agentsResp?.data?.map(agentResponseToAgent) ?? [],
    [agentsResp, options.agents]
  )

  const currentAgent = useMemo(
    () =>
      options.currentAgent ?? agents.find((a) => a.id.toString() === agentId),
    [agents, agentId, options.currentAgent]
  )

  // Evaluators
  const {
    data: evaluatorsResp,
    error: evaluatorsError,
    fetchFn: getEvaluators,
  } = useGetEvaluatorsApi()

  const [evaluators, setEvaluators] = useState<Evaluator[]>([])
  const [selectedEvaluator, setSelectedEvaluator] = useState<Evaluator | null>(
    null
  )
  const [labels, setLabels] = useState<EvaluationCaseLabel[]>([])
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([])

  const {
    data: labelsResp,
    error: labelsError,
    fetchFn: getLabels,
  } = useListEvaluationCaseLabelsApi(workspaceId)

  const refreshLabels = useCallback(() => {
    if (workspaceId) {
      getLabels()
    }
  }, [workspaceId, getLabels])

  const getCaseSearchParams = useCallback(() => {
    const params = new URLSearchParams({ wid: workspaceId || "" })
    selectedLabelIds.forEach((labelId) => params.append("label_ids", labelId))
    return params
  }, [workspaceId, selectedLabelIds])

  const refreshEvaluators = useCallback(() => {
    if (workspaceId) {
      getEvaluators({ searchParams: new URLSearchParams({ wid: workspaceId }) })
    }
  }, [workspaceId, getEvaluators])

  // Cases and Results
  const {
    data: casesResp,
    error: casesError,
    fetchFn: fetchCases,
  } = useListEvaluationCasesApi(agentId)

  const { error: resultsError, fetchFn: fetchResults } =
    useGetEvaluationResultsByCasesApi()

  const { data: caseEvaluatorInfosResp, fetchFn: fetchCaseEvaluatorInfos } =
    useGetCaseEvaluatorInfosByCasesApi()

  const [cases, setCases] = useState<EvaluationCase[]>([])
  const [totalCaseCount, setTotalCaseCount] = useState(0)
  const [caseEvaluatorInfosByCase, setCaseEvaluatorInfosByCase] = useState<
    Record<string, CaseEvaluatorAssignment[]>
  >({})
  const [results, setResults] = useState<EvaluationResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchEvaluationResultsForScope = useCallback(
    async (scope: EvaluationResultScope) => {
      if (!workspaceId) {
        console.error(
          "Cannot fetch evaluation results: workspaceId is required but not available"
        )
        return []
      }

      const requestPayload = makeGetEvaluationResultsByCasesRequest(
        scope.targets,
        workspaceId,
        scope.includeOutput ?? true,
        scope.includeReasoningSteps ?? false
      )

      const response = await fetchResults({
        jsonBody: requestPayload,
        preserveData: true,
      })
      const evaluationResults = response?.data
        ? transToEvaluationResults(response.data).map((result) => ({
            ...result,
            agentHistoryId:
              result.agentHistoryId ??
              scope.targets.find(
                (target) =>
                  target.caseId === result.evaluationCaseId &&
                  target.evaluatorId === result.evaluatorId
              )?.agentHistoryId,
          }))
        : []

      setResults((currentResults) =>
        mergeEvaluationResultsForScope(currentResults, evaluationResults, scope)
      )

      return evaluationResults
    },
    [fetchResults, workspaceId]
  )

  // Effects for data fetching
  useEffect(() => {
    if (workspaceId) {
      refreshAgents()
    }
  }, [workspaceId, refreshAgents])

  useEffect(() => {
    if (workspaceId) {
      refreshEvaluators()
    }
  }, [workspaceId, refreshEvaluators])

  useEffect(() => {
    if (workspaceId) {
      refreshLabels()
    }
  }, [workspaceId, refreshLabels])

  useEffect(() => {
    if (evaluatorsResp?.data) {
      const evaluators = evaluatorsResponseToEvaluators(evaluatorsResp.data)
      setEvaluators(evaluators)
    }
  }, [evaluatorsResp])

  useEffect(() => {
    if (labelsResp?.data) {
      setLabels(labelsResp.data.map(evaluationCaseLabelResponseToData))
    }
  }, [labelsResp])

  useEffect(() => {
    const labelIds = new Set(labels.map((label) => label.id))
    setSelectedLabelIds((current) =>
      current.filter((labelId) => labelIds.has(labelId))
    )
  }, [labels])

  useEffect(() => {
    if (agentId && workspaceId) {
      fetchCases({ searchParams: getCaseSearchParams() })
    }
  }, [agentId, workspaceId, fetchCases, getCaseSearchParams])

  // When cases are loaded, set cases and fetch results if evaluator is selected
  useEffect(() => {
    if (!casesResp?.data || !workspaceId) return

    const evaluationCases = transToEvaluationCases(casesResp.data.cases)
    setCases(evaluationCases)
    setTotalCaseCount(casesResp.data.total_case_count)
    setLoading(false) // Always set loading to false when cases are loaded

    // If no cases, don't fetch results
    if (evaluationCases.length === 0) {
      setResults([])
      setCaseEvaluatorInfosByCase({})
      return
    }
  }, [casesResp, workspaceId])

  // ------------------------------
  // BUG FIX NOTE
  // ------------------------------
  // The auto-save feature inside the Case Detail Modal updates a field of the
  // selected case (e.g. input, rubric). That update replaces the `cases` array
  // with a new reference even though the *set* of case IDs is unchanged. If we
  // left `cases` in the dependency array of the effect that fetches evaluation
  // results, **every auto-save would retrigger the effect**, flip `loading` to
  // true and temporarily unmount the EvaluationCaseTable component — which in
  // turn closes the modal.
  //
  // To avoid that, derive a stable representation that only changes when case
  // IDs are added/removed: a sorted, comma-separated string of IDs. The effect
  // now depends on `caseIdString` instead of the full `cases` array, so normal
  // field edits no longer close the modal while structural changes still force
  // a refetch.
  // ------------------------------
  // Memoized comma-separated id list – updates only when the set of case ids changes
  const caseIdString = useMemo(() => {
    // Sort to avoid dependency changes caused by different orderings
    return cases
      .map((c) => c.id)
      .sort()
      .join(",")
  }, [cases])

  useEffect(() => {
    const caseIds = caseIdString.length > 0 ? caseIdString.split(",") : []
    if (caseIds.length === 0) return

    fetchCaseEvaluatorInfos({
      jsonBody: makeGetCaseEvaluatorInfosByCasesRequest(caseIds),
    }).catch((error) => {
      console.error("Failed to fetch case evaluator info:", error)
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("evaluation.evaluationTable.message.fetchResultsError"),
          fallbackDescription: t(
            "evaluation.evaluationTable.message.fetchResultsErrorDesc"
          ),
        }),
      })
    })
  }, [caseIdString, fetchCaseEvaluatorInfos, alertToast, t])

  // selectedEvaluator useEffect
  useEffect(() => {
    // Only fetch results if an evaluator is selected
    if (!selectedEvaluator) {
      setResults([])
      return
    }

    // Only fetch results if an agent history is selected
    if (!selectedAgentHistory?.id || !workspaceId) {
      console.error(
        "Cannot fetch evaluation results: agent_history_id or workspaceId is required but not available"
      )
      setResults([])
      return
    }

    // Convert id string back to array
    const caseIds = caseIdString.length > 0 ? caseIdString.split(",") : []

    if (caseIds.length === 0) {
      setResults([])
      return
    }
    setLoading(true)
    setError(null)

    fetchEvaluationResultsForScope({
      targets: caseIds.map((caseId) => ({
        caseId,
        evaluatorId: selectedEvaluator.id,
        agentHistoryId: selectedAgentHistory.id,
      })),
      includeOutput: true,
    })
      .catch((error) =>
        setError(
          getUserFacingApiErrorMessage(
            error,
            t,
            "Failed to fetch evaluation results."
          )
        )
      )
      .finally(() => setLoading(false))
  }, [
    selectedEvaluator,
    selectedAgentHistory,
    workspaceId,
    caseIdString,
    fetchEvaluationResultsForScope,
    t,
  ])

  useEffect(() => {
    if (!caseEvaluatorInfosResp?.data) return
    const caseEvaluatorInfos: CaseEvaluatorInfosByCase[] =
      caseEvaluatorInfosResponseToData(caseEvaluatorInfosResp.data)
    setCaseEvaluatorInfosByCase(
      Object.fromEntries(
        caseEvaluatorInfos.map((caseInfo) => [
          caseInfo.caseId,
          caseInfo.evaluators,
        ])
      )
    )
  }, [caseEvaluatorInfosResp])

  // Merge results into cases for table display
  const mergedCases: MergedEvaluationCase[] = useMemo(() => {
    if (!cases.length) return []
    if (!selectedEvaluator) {
      return cases.map((c) => ({
        ...c,
        assignedEvaluators: caseEvaluatorInfosByCase[c.id] || [],
      }))
    }
    // Only use results for the selected evaluator
    const resultMap: Record<string, EvaluationResult> = {}
    results.forEach((r) => {
      if (
        r.evaluatorId === selectedEvaluator.id &&
        r.agentHistoryId === selectedAgentHistory?.id
      ) {
        resultMap[r.evaluationCaseId] = r
      }
    })

    const merged = cases.map((c) => {
      const assignedEvaluators = caseEvaluatorInfosByCase[c.id] || []
      const selectedEvaluatorAssignment = assignedEvaluators.find(
        (assignment) => assignment.evaluatorId === selectedEvaluator.id
      )
      const selectedEvaluatorAssigned = !!selectedEvaluatorAssignment
      return {
        ...c,
        assignedEvaluators,
        score: selectedEvaluatorAssigned ? resultMap[c.id]?.score : undefined,
        reason: selectedEvaluatorAssigned ? resultMap[c.id]?.reason : undefined,
        rubric: selectedEvaluatorAssignment?.rubric || "",
        selectedEvaluatorAssigned,
      }
    })
    return merged
  }, [
    cases,
    results,
    selectedEvaluator,
    selectedAgentHistory,
    caseEvaluatorInfosByCase,
  ])

  // Set default selected evaluator when evaluators are loaded
  useEffect(() => {
    const storedEvaluatorId = localStorage.getItem(
      STORAGE_KEYS.SELECTED_EVALUATOR
    )

    if (storedEvaluatorId === ALL_EVALUATORS_STORAGE_VALUE) {
      if (selectedEvaluator) setSelectedEvaluator(null)
      return
    }

    const evaluatorId = selectedEvaluator?.id || storedEvaluatorId
    const nextEvaluator = evaluators.find(
      (evaluator) => evaluator.id === evaluatorId
    )
    if (nextEvaluator) {
      if (nextEvaluator !== selectedEvaluator) {
        setSelectedEvaluator(nextEvaluator)
      }
      return
    }

    setSelectedEvaluator(evaluators[0] || null)
  }, [evaluators, selectedEvaluator])

  // Error handling
  const handleApiErrors = useCallback(() => {
    if (agentsError) {
      console.error("Failed to fetch agents", agentsError)
      alertToast({
        title: t("evaluation.evaluationTable.message.fetchAgentsError"),
        description: getUserFacingApiErrorMessage(
          agentsError,
          t,
          t("evaluation.evaluationTable.message.fetchAgentsErrorDesc")
        ),
      })
    }

    if (evaluatorsError) {
      console.error("Failed to fetch evaluators", evaluatorsError)
      alertToast({
        title: t("evaluation.evaluationTable.message.fetchEvaluatorsError"),
        description: getUserFacingApiErrorMessage(
          evaluatorsError,
          t,
          t("evaluation.evaluationTable.message.fetchEvaluatorsErrorDesc")
        ),
      })
    }

    if (casesError) {
      console.error("Failed to fetch evaluation cases", casesError)
      alertToast({
        title: t("evaluation.evaluationTable.message.fetchCasesError"),
        description: getUserFacingApiErrorMessage(
          casesError,
          t,
          t("evaluation.evaluationTable.message.fetchCasesErrorDesc")
        ),
      })
    }

    if (resultsError) {
      console.error("Failed to fetch evaluation results", resultsError)
      alertToast({
        title: t("evaluation.evaluationTable.message.fetchResultsError"),
        description: getUserFacingApiErrorMessage(
          resultsError,
          t,
          t("evaluation.evaluationTable.message.fetchResultsErrorDesc")
        ),
      })
    }
    if (labelsError) {
      console.error("Failed to fetch evaluation case labels", labelsError)
      alertToast({
        title: t("evaluation.evaluationTable.message.fetchLabelsError"),
        description: getUserFacingApiErrorMessage(
          labelsError,
          t,
          t("evaluation.evaluationTable.message.fetchLabelsErrorDesc")
        ),
      })
    }
  }, [
    agentsError,
    evaluatorsError,
    casesError,
    resultsError,
    labelsError,
    alertToast,
    t,
  ])

  return {
    agents,
    currentAgent,
    agentsError,
    refreshAgents,
    selectedAgentHistory,
    setSelectedAgentHistory,
    evaluators,
    selectedEvaluator,
    setSelectedEvaluator,
    evaluatorsError,
    refreshEvaluators,
    labels,
    selectedLabelIds,
    setSelectedLabelIds,
    refreshLabels,
    getCaseSearchParams,
    cases,
    totalCaseCount,
    setCases,
    caseEvaluatorInfosByCase,
    setCaseEvaluatorInfosByCase,
    results,
    setResults,
    mergedCases,
    loading,
    setLoading,
    error,
    setError,
    fetchCases,
    fetchEvaluationResultsForScope,
    fetchCaseEvaluatorInfos,
    handleApiErrors,
  }
}
